import { Fragment, useCallback, useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import userRound from '~/assets/icons/user_round.svg';
import { playSound, Sound } from '~/assets/sounds';
import { CurrencyIcon } from '~/components/collections/CurrencyIcon';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage } from '~/rage';
import { Currency, numWord } from '~/shared';
import {
	StyledBalanceContainer,
	StyledContainer,
	StyledHead,
	StyledInfo,
	StyledInfoItem,
	StyledPopup,
} from './Character.styled';

const formatLastLogin = (timestamp: number | null) => {
	if (!timestamp) {
		return { time: null, date: 'Никогда' };
	}

	const date = new Date(timestamp);
	const time = date.toLocaleTimeString('ru-RU', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		timeZone: 'Europe/Moscow',
	});
	const formattedDate = date.toLocaleDateString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		year: '2-digit',
		timeZone: 'Europe/Moscow',
	});

	return { time, date: formattedDate };
};

const Character = ({
	id,
	name,
	level,
	isStaff,
	balance,
	lastEnteredAt,
	banExpiresAt,
	banReason,
	isOpen,
	onClick,
}: {
	id: number;
	name: string;
	level: number;
	isStaff: boolean;
	balance: number;
	lastEnteredAt: number | null;
	banExpiresAt: number | null | undefined;
	banReason: string | null;
	isOpen?: boolean;
	onClick?: () => void;
}) => {
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const [isBanActive, setIsBanActive] = useState(false);
	const [banTimerText, setBanTimerText] = useState('');
	const [popupVisible, setPopupVisible] = useState(false);

	const handleButtonHover = useCallback(() => {
		if (banReason) {
			setPopupVisible(true);
		}
	}, [banReason]);

	const handleButtonLeave = useCallback(() => {
		setPopupVisible(false);
	}, []);

	const { time: lastLoginTime, date: lastLoginDate } =
		formatLastLogin(lastEnteredAt);

	useEffect(() => {
		if (banExpiresAt === undefined) {
			setIsBanActive(false);
			setBanTimerText('');
			return;
		}

		const minuteMs = 60 * 1000;
		const hourMs = 60 * minuteMs;
		const dayMs = 24 * hourMs;

		const updateBanTimer = () => {
			const diff = banExpiresAt !== null ? banExpiresAt - Date.now() : -1;

			if (banExpiresAt !== null && diff <= 0) {
				setIsBanActive(false);
				setBanTimerText('');
				return;
			}

			setIsBanActive(true);

			if (diff === -1) {
				setBanTimerText('навсегда');
				return;
			}

			if (diff >= dayMs) {
				const days = Math.ceil(diff / dayMs);
				setBanTimerText(
					`${days.toLocaleString('ru-RU')} ${numWord(days, [
						'день',
						'дня',
						'дней',
					])}`,
				);
				return;
			}

			if (diff >= hourMs) {
				const hours = Math.ceil(diff / hourMs);
				setBanTimerText(
					`${hours.toLocaleString('ru-RU')} ${numWord(hours, [
						'час',
						'часа',
						'часов',
					])}`,
				);
				return;
			}

			if (diff >= minuteMs) {
				const minutes = Math.ceil(diff / minuteMs);
				setBanTimerText(
					`${minutes.toLocaleString('ru-RU')} ${numWord(minutes, [
						'минута',
						'минуты',
						'минут',
					])}`,
				);
				return;
			}

			setBanTimerText(`1 минута`);
		};

		if (banExpiresAt === null) {
			updateBanTimer();
			return;
		}

		updateBanTimer();

		const interval = setInterval(updateBanTimer, 60000);

		return () => {
			clearInterval(interval);
		};
	}, [banExpiresAt]);

	return (
		<StyledContainer
			data-character-select-card
			$open={isOpen || false}
			onClick={() => {
				onClick?.();
				playSound(Sound.ClickButton);
			}}
			onMouseEnter={() => {
				if (!isOpen) {
					playSound(Sound.LinkCursor);
				}
			}}
		>
			<StyledHead>
				<img src={userRound} />
				<Text style={{ fontWeight: 500, fontSize: 16 }}>{name}</Text>
				<Text style={{ fontWeight: 500 }}>
					{isOpen && (
						<Fragment>
							{/* TODO: сделать группы */}
							{isStaff ? 'Админ' : 'Игрок'}{' '}
							<span style={{ opacity: 0.25 }}>/</span>{' '}
						</Fragment>
					)}
					{level} <span style={{ opacity: 0.5, fontWeight: 400 }}>ур.</span>
				</Text>
			</StyledHead>
			<Divider $direction="H" style={{ opacity: 0.1, margin: '20px 0' }} />
			<StyledInfo>
				<StyledInfoItem>
					<Text style={{ opacity: 0.5 }}>ID</Text>
					<Text style={{ fontWeight: 500 }}>#{id}</Text>
				</StyledInfoItem>
				<StyledInfoItem>
					<Text style={{ opacity: 0.5 }}>Деньги</Text>
					<StyledBalanceContainer>
						<Text>{balance.toLocaleString('ru-RU')}</Text>
						<CurrencyIcon icon={rubleIcon} size="sm" />
					</StyledBalanceContainer>
				</StyledInfoItem>
				<StyledInfoItem>
					<Text style={{ opacity: 0.5 }}>Последний вход</Text>
					<Text style={{ fontWeight: 500 }}>
						{lastLoginTime && (
							<>
								{lastLoginTime} <span style={{ opacity: 0.25 }}>/</span>{' '}
							</>
						)}
						{lastLoginDate}
					</Text>
				</StyledInfoItem>
			</StyledInfo>
			{isBanActive ? (
				<div onMouseEnter={handleButtonHover} onMouseLeave={handleButtonLeave}>
					<Button
						color={ButtonColors.Default}
						height={ButtonHeight.Height40}
						style={{ marginTop: 26, borderRadius: 5 }}
						disabled
					>
						<Text>
							Блокировка <span>{banTimerText}</span>
						</Text>
					</Button>
				</div>
			) : (
				<Button
					color={ButtonColors.Select}
					height={ButtonHeight.Height40}
					style={{ marginTop: 26, borderRadius: 5 }}
					onClick={() => {
						rage.characterSelection
							.selectCharacter(id.toString())
							.catch((err) => rage.notifications.showException(err));
					}}
				>
					<Text>Выбрать персонажа</Text>
				</Button>
			)}
			<CSSTransition
				in={popupVisible}
				classNames={'popup'}
				timeout={500}
				unmountOnExit
			>
				<StyledPopup text={banReason || ''} />
			</CSSTransition>
		</StyledContainer>
	);
};

export { Character };
